import React, { useEffect, useState } from "react";
import { AlertTriangle, TrendingDown } from "lucide-react";
import { Card, Pill } from "./common/Primitives";
import api from "../lib/api";

/**
 * Anonymised rejection feedback for founders.
 * Fetches /startup/rejection-feedback and renders:
 *   - Total investor passes
 *   - Top 3 reasons (weighted by primary/secondary)
 *   - One-liner insights ("3 investors passed due to X")
 */
export default function RejectionFeedbackCard({ testId = "rejection-feedback" }) {
  const [view, setView] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/startup/rejection-feedback")
      .then((r) => setView(r.data))
      .catch(() => setView({ total_rejections: 0, top_reasons: [], insights: [] }))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  const empty = !view || view.total_rejections === 0;

  return (
    <Card testId={testId} className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><TrendingDown size={12} /> INVESTOR FEEDBACK</div>
          <div className="font-heading text-ink text-lg mt-1">
            {empty ? "No investor passes yet" : `${view.total_rejections} investor${view.total_rejections === 1 ? "" : "s"} passed`}
          </div>
        </div>
        {!empty && <Pill tone="warn" testId="rejection-total">{view.total_rejections}</Pill>}
      </div>

      {empty ? (
        <div className="text-xs text-ink-soft">Once investors provide feedback, you&rsquo;ll see anonymised patterns here — never who said what.</div>
      ) : (
        <>
          {view.insights?.length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid="rejection-insights">
              {view.insights.map((line, i) => (
                <div key={i} className="text-xs text-ink-soft flex items-start gap-2">
                  <AlertTriangle size={11} className="text-amber-600 shrink-0 mt-0.5" />
                  <span>{line}</span>
                </div>
              ))}
            </div>
          )}
          <div className="grid gap-1.5" data-testid="rejection-top-reasons">
            {view.top_reasons.slice(0, 5).map((r) => (
              <div key={r.key} className="flex items-center justify-between px-3 py-2 rounded border border-navy/[0.08]"
                    data-testid={`reason-${r.key}`}>
                <span className="text-sm text-ink">{r.label}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1.5 rounded bg-navy/5 overflow-hidden">
                    <div className="h-full bg-amber-500"
                          style={{ width: `${Math.min(100, Math.round((r.weighted_score / (view.top_reasons[0]?.weighted_score || 1)) * 100))}%` }} />
                  </div>
                  <span className="text-xs font-heading text-ink-soft w-8 text-right">{r.count}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
